// Demo performance optimization utilities

export const DEMO_CONFIG = {
  // Faster timeouts for demo performance
  AUTH_TIMEOUT: 3000,
  PROFILE_TIMEOUT: 2000,
  BRAND_TIMEOUT: 1500,
  
  // Demo fallback data
  DEMO_USER: {
    id: 'demo-user-123',
    email: 'sarah.johnson@example.com',
    first_name: 'Sarah',
    last_name: 'Johnson',
    role: 'brand_user'
  },
  
  DEMO_BRAND: {
    id: 'demo-brand',
    name: 'Demo Health Brand',
    personalizedName: 'Your Health Partner',
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    industry: 'health',
    framework: 'sense',
    createdAt: new Date().toISOString(),
    logoUrl: '',
    aiChatText: 'Welcome! How can I help you with your health today?',
    aiFollowUpText: 'I\'m here to support your wellness journey.',
    userChatText: 'How can I track my health today?'
  }
};

// Check if we should use demo mode
export const shouldUseDemoMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return (
    urlParams.get('demo') === 'true' ||
    window.location.hash.includes('demo')
  );
};

// Performance monitoring for demos
export const trackDemoPerformance = (action: string, startTime: number) => {
  const endTime = performance.now();
  const duration = endTime - startTime;
  
  if (duration > 2000) {
    console.warn(`🎭 Demo performance warning: ${action} took ${duration.toFixed(0)}ms`);
  } else if (import.meta.env.DEV) {
    console.log(`🎭 Demo timing: ${action} completed in ${duration.toFixed(0)}ms`);
  }
};

// Create performance-optimized promises with fallbacks
export const createFallbackPromise = <T>(
  promise: Promise<T>,
  fallback: T,
  timeout: number = 2000
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(() => {
        console.warn(`🎭 Using fallback after ${timeout}ms timeout`);
        resolve(fallback);
      }, timeout);
    })
  ]);
};

// Preload critical assets for better demo performance
export const preloadDemoAssets = () => {
  // Preload common images that might be used in demos
  const imagesToPreload = [
    '/icons/icon-192x192.png',
    '/icons/icon-512x512.png'
  ];
  
  imagesToPreload.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};

// Initialize demo optimizations
export const initializeDemoOptimizations = () => {
  if (shouldUseDemoMode()) {
    console.log('🎭 Demo mode initialized with performance optimizations');
    preloadDemoAssets();
    
    // Add demo mode indicator to help with debugging
    document.body.classList.add('demo-mode');
  }
};